import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template58: Página de Odaiba de Tokio
// Copia DIRECTA del componente Page19.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template58 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [monorailError, setMonorailError] = useState(false);
	const [odaiba2Error, setOdaiba2Error] = useState(false);
	const [odaibaError, setOdaibaError] = useState(false);
	const [teamLabError, setTeamLabError] = useState(false);
	const [odaibaBadgeError, setOdaibaBadgeError] = useState(false);
	const [odaibaStickerError, setOdaibaStickerError] = useState(false);

	const headerText = page.texts?.[0]?.content;
	const logoImage =
		page.canvasImages?.find((img) => img.id === "logo")?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
			? page.data.order
			: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0
			? "#e24217"
			: headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const odaibaImage = getImageSrc("odaiba-main");
	const odaiba2Image = getImageSrc("odaiba-2");
	const monorailImage = getImageSrc("monorail");
	const odaibaBadgeImage = getImageSrc("odaiba-badge");
	const teamLabImage = getImageSrc("team-lab");
	const odaibaSticker = getCanvasImageUrl("odaiba-sticker");

	const introText = page.texts?.[1]?.content;
	const odaibaTitle = page.texts?.[2]?.content;
	const odaibaSubtitle = page.texts?.[3]?.content;
	const odaibaText = page.texts?.[4]?.content;
	const atraccionesTitle = page.texts?.[5]?.content;
	const atraccionesText = page.texts?.[6]?.content;
	const sabiasQueText = page.texts?.[7]?.content;
	const monorailBoxText = page.texts?.[8]?.content;
	const stickerCentrosText = page.texts?.[9]?.content;
	const gundamTitle = page.texts?.[10]?.content;
	const gundamText = page.texts?.[11]?.content;
	const sabiasQueGundamHtml = page.texts?.[12]?.content;
	const estatuaTitle = page.texts?.[13]?.content;
	const estatuaText = page.texts?.[14]?.content;
	const sabiasQueEstatuaHtml = page.texts?.[15]?.content;
	const sabiasQueTeamLabHtml = page.texts?.[16]?.content;
	const stickerTeamLabText = page.texts?.[17]?.content;
	const teamLabPlanetsTitle = page.texts?.[18]?.content;
	const teamLabPlanetsContent = page.texts?.[19]?.content;

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch) return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};
	const sabiasQueGundam = parseSabiasQueItem(sabiasQueGundamHtml);
	const sabiasQueEstatua = parseSabiasQueItem(sabiasQueEstatuaHtml);
	const sabiasQueTeamLab = parseSabiasQueItem(sabiasQueTeamLabHtml);

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
			height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
			</Box>

			{/* INTRO */}
			<Box data-block-id="text-1" sx={{ padding: "14px 12px", display: "flex", gap: "16px" }}>
				<Box sx={{ width: "100%" }}>
					<Box component="h3" sx={estiloH3}>
						{odaibaTitle}
					</Box>
					{introText && (
						<Box component="div" dangerouslySetInnerHTML={{ __html: introText }} sx={estiloP} />
					)}
				</Box>
			</Box>

			{/* Monorail */}
			<Box
				data-block-id="text-2"
				sx={{
					backgroundColor: "#fff8db",
					border: "2px solid #f5c400",
					borderRadius: "16px",
					padding: "14px",
					margin: "16px 12px",
					marginTop: 0,
					fontSize: 13,
					fontFamily: `${fontFamily} !important`,
					display: "flex",
					gap: "12px",
					position: "relative",
				}}
			>
				{monorailImage && !monorailError ? (
					<img
						src={monorailImage}
						alt="Monorail"
						onError={() => setMonorailError(true)}
						style={{
							width: 100,
							height: 100,
							borderRadius: "12px",
							objectFit: "cover",
						}}
					/>
				) : (
					<ImagenNoDisponibleKawaii width={100} height={100} fontFamily={fontFamily} compact />
				)}
				{monorailBoxText && (
					<Box component="div" dangerouslySetInnerHTML={{ __html: monorailBoxText }} sx={{ flex: 1 }} />
				)}

				{odaibaBadgeImage && !odaibaBadgeError && (
					<img
						src={odaibaBadgeImage}
						alt=""
						onError={() => setOdaibaBadgeError(true)}
						style={{
							width: 100,
							position: "absolute",
							bottom: -80,
							left: -50,
						}}
					/>
				)}

				{odaibaSticker && !odaibaStickerError && (
					<img
						src={odaibaSticker}
						alt=""
						onError={() => setOdaibaStickerError(true)}
						style={{
							width: 80,
							position: "absolute",
							bottom: isSmallScreen ? -70 : -50,
							right: -20,
						}}
					/>
				)}
				{stickerCentrosText && (
					<Box
						sx={{
							position: "absolute",
							bottom: isSmallScreen ? "-55px" : "-20px",
							right: "60px",
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "6px 10px",
							fontSize: 11,
							fontFamily: `${fontFamily} !important`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 180,
						}}
					>
						{stickerCentrosText}
					</Box>
				)}
			</Box>

			{/* Gundam */}
			<Box
				data-block-id="text-3"
				sx={{
					padding: "14px 12px",
					display: "flex",
					gap: "16px",
					marginTop: isSmallScreen ? "40px" : 0,
				}}
			>
				{odaiba2Image && !odaiba2Error ? (
					<img
						src={odaiba2Image}
						alt="Gundam"
						onError={() => setOdaiba2Error(true)}
						style={{
							width: "45%",
							borderRadius: "12px",
							objectFit: "cover",
							height: 110,
						}}
					/>
				) : (
					<ImagenNoDisponibleKawaii width="45%" height={110} fontFamily={fontFamily} compact />
				)}
				<Box sx={{ width: "55%" }}>
					{gundamTitle && (
						<Box component="h3" sx={estiloH3}>
							{gundamTitle}
						</Box>
					)}
					{gundamText && <Box component="p" sx={estiloP}>{gundamText}</Box>}
					{sabiasQueGundam && (
						<SabiasQue icono={sabiasQueGundam.icono} texto={sabiasQueGundam.texto} />
					)}
				</Box>
			</Box>

			{/* Estatua de la Libertad */}
			<Box
				data-block-id="text-4"
				sx={{
					padding: "14px 12px",
					display: "flex",
					gap: "16px",
					position: "relative",
				}}
			>
				{odaibaImage && !odaibaError ? (
					<img
						src={odaibaImage}
						alt="Odaiba"
						onError={() => setOdaibaError(true)}
						style={{
							width: "45%",
							borderRadius: "12px",
							objectFit: "cover",
							height: 110,
						}}
					/>
				) : (
					<ImagenNoDisponibleKawaii width="45%" height={110} fontFamily={fontFamily} compact />
				)}
				<Box sx={{ width: "55%" }}>
					{estatuaTitle && (
						<Box component="h3" sx={estiloH3}>
							{estatuaTitle}
						</Box>
					)}
					{estatuaText && <Box component="p" sx={estiloP}>{estatuaText}</Box>}
					{sabiasQueEstatua && (
						<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
							<SabiasQue icono={sabiasQueEstatua.icono} texto={sabiasQueEstatua.texto} />
						</Box>
					)}
				</Box>
				{sabiasQueTeamLab && (
					<Box sx={{ position: "absolute", bottom: 0, left: "33%" }}>
						<SabiasQue icono={sabiasQueTeamLab.icono} texto={sabiasQueTeamLab.texto} />
					</Box>
				)}
				{teamLabImage && !teamLabError && (
					<img
						src={teamLabImage}
						alt=""
						onError={() => setTeamLabError(true)}
						style={{
							width: isSmallScreen ? 100 : 100,
							position: "absolute",
							bottom: isSmallScreen ? -100 : -50,
							right: -20,
						}}
					/>
				)}
				{stickerTeamLabText && (
					<Box
						sx={{
							position: "absolute",
							bottom: isSmallScreen ? -40 : "-10px",
							right: isSmallScreen ? "20px" : "60px",
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "6px 10px",
							fontSize: 11,
							fontFamily: `${fontFamily} !important`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 180,
						}}
						component="div"
						dangerouslySetInnerHTML={{ __html: stickerTeamLabText }}
					/>
				)}
			</Box>

			{/* TeamLab Planets */}
			<Box sx={{ padding: "0px 12px" }}>
				<Box
					sx={{
						backgroundColor: "#ffe3cc",
						border: "3px solid #f28b35",
						borderRadius: "12px",
						padding: "16px 20px",
						fontSize: 12,
						fontFamily: `${fontFamily} !important`,
						color: "#333",
						marginTop: isSmallScreen ? "60px" : 0,
						marginBottom: isSmallScreen ? "12px" : 0,
					}}
				>
					{teamLabPlanetsTitle && (
						<Box
							component="h4"
							sx={{
								margin: 0,
								marginBottom: "8px",
								fontSize: 13,
								fontWeight: 700,
								color: "#f28b35",
								textTransform: "uppercase",
								fontFamily: `${fontFamily} !important`,
							}}
						>
							{teamLabPlanetsTitle}
						</Box>
					)}
					{teamLabPlanetsContent && (
						<Box
							component="div"
							dangerouslySetInnerHTML={{ __html: teamLabPlanetsContent }}
							sx={{ ...estiloP, "& a": { color: "#f28b35", fontWeight: 600, textDecoration: "underline" } }}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template58.thumbnail = "/images/template-1.png";

export default Template58;
